import { ApiSection } from "@/components/kudoz/ApiSection";
import { MethodBadge } from "@/components/kudoz/MethodBadge";
import { KUDOZ_BACKEND_CONFIG } from "@/services/kudozBackendEndpoints";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CodeBlock } from "@/components/kudoz/CodeBlock";

type EndpointDef = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  params?: string[];
  requestExample?: object;
  responseExample?: object;
};

type ApiGroup = {
  title: string;
  description: string;
  endpoints: EndpointDef[];
};

const API_GROUPS: ApiGroup[] = [
  {
    title: "1. Users (Wallets & Ledger)",
    description: "Manage user balances and view transaction history",
    endpoints: [
      {
        method: "GET",
        path: "/users/:user_id/kudoz",
        description: "Get user's current Kudoz balance",
        params: ["user_id"],
        responseExample: {
          success: true,
          wallet: {
            wallet_id: "user-123",
            user_id: 123,
            wallet_type: "user",
            balance: 1500,
            lifetime_earned: 5000,
            lifetime_spent: 3500,
            is_active: true,
            created_at: "2023-01-01T00:00:00Z",
            updated_at: "2023-01-15T12:00:00Z",
          },
          pending_amount: 0,
        },
      },
      {
        method: "GET",
        path: "/users/:user_id/kudoz-transactions",
        description: "Get user's transaction ledger (history)",
        params: ["user_id", "limit", "offset"],
        responseExample: {
          success: true,
          transactions: [
            {
              transaction_id: "txn_789",
              amount: 100,
              transaction_type: "earn",
              description: "Order reward #1001",
              created_at: "2023-01-15T12:00:00Z",
            },
          ],
          total: 1,
          limit: 50,
          offset: 0,
        },
      },
      {
        method: "POST",
        path: "/transfers",
        description: "Transfer Kudoz. **Dual-Auth**: For System (x-services-secret), 'sender' is REQUIRED. For User (Bearer), 'sender' is OPTIONAL (inferred from token).",
        requestExample: {
          sender: {
            type: "business",
            id: 10,
            _comment: "REQUIRED for System Auth. OPTIONAL for User Auth.",
          },
          receiver: {
            type: "restaurant",
            id: 5,
          },
          amount: 500.0,
          description: "Settlement for Order #123",
        },
        responseExample: {
          success: true,
          message: "Transfer successful",
          data: {
            success: true,
            message: "Transfer successful",
          },
        },
      },
    ],
  },
  {
            title: "2. Fetch delivery fee, other fee and tax values to create cart",
            description: "Calculate fees and taxes for cart creation (Eatzy API)",
            endpoints: [
              {
                method: "POST",
                path: "/restaurant/customer/checkout/start",
                description: "Initialize checkout and calculate initial fees/taxes",
                requestExample: {
                  client: "restaurant_123",
                  data: {
                    type: "target",
                    mode: "delivery",
                    address: 101,
                    phone: 202,
                    start: 1735689600,
                    end: 1735693200,
                    cart: [
                      {
                        id: "I1001",
                        items: [{ sort: 1, quantity: 2, id: "I1001" }]
                      }
                    ]
                  }
                },
                responseExample: {
                  id: "chk_abc123",
                  restaurant: "restaurant_123",
                  subtotal: 25.5,
                  total: 32.15,
                  fees: [
                    {
                      id: 10,
                      name: "Delivery Fee",
                      amount: 2.99,
                      type: "fee",
                      percent: 0
                    },
                    {
                      id: 11,
                      name: "Service Fee",
                      amount: 1.28,
                      type: "fee",
                      percent: 5.0
                    }
                  ],
                  taxes: [
                    {
                      id: 1,
                      name: "Sales Tax",
                      amount: 2.38,
                      scope: "item",
                      targets: [{ type: "item", id: "I1001", amount: 2.38 }]
                    }
                  ],
                  schedule: {
                    takeout: 1735689600,
                    delivery: 1735691400
                  },
                  items: [
                    {
                      id: "I1001",
                      quantity: 2,
                      price: 12.75,
                      total: 25.5
                    }
                  ],
                  coupons: [],
                  payments: [],
                  gift_certificate: null
                }
              },
              {
                method: "POST",
                path: "/restaurant/customer/checkout/adjust",
                description: "Adjust checkout with tips or other changes",
                requestExample: {
                  client: "restaurant_123",
                  checkout: "chk_abc123",
                  data: {
                    fees: [
                      {
                        id: 10,
                        amount: 2.99
                      },
                      {
                        id: 99,
                        amount: 5.0
                      }
                    ]
                  }
                },
                responseExample: {
                  id: "chk_abc123",
                  restaurant: "restaurant_123",
                  subtotal: 25.5,
                  total: 37.15,
                  fees: [
                    {
                      id: 10,
                      name: "Delivery Fee",
                      amount: 2.99,
                      type: "fee",
                      percent: 0
                    },
                    {
                      id: 99,
                      name: "Driver Tip",
                      amount: 5.0,
                      type: "tip",
                      percent: 0
                    }
                  ],
                  taxes: [
                    {
                      id: 1,
                      name: "Sales Tax",
                      amount: 2.38
                    }
                  ],
                  schedule: {
                    takeout: 1735689600,
                    delivery: 1735691400
                  },
                  items: [
                    {
                      id: "I1001",
                      quantity: 2,
                      price: 12.75,
                      total: 25.5
                    }
                  ],
                  coupons: [],
                  payments: [],
                  gift_certificate: null
                }
              }
            ]
          },
  {
    title: "Earn (Accumulation)",
    description: "Calculate and award Kudoz points",
    endpoints: [
      {
        method: "POST",
        path: "/kudoz-earns/calculate",
        description: "Calculate potential earnings for an order",
      },
      {
        method: "POST",
        path: "/kudoz-earns/preview",
        description: "Preview earning outcome before committing",
      },
      {
        method: "POST",
        path: "/kudoz-earns/award",
        description: "Award Kudoz to a user (Updates Wallet/Ledger)",
      },
    ],
  },
  {
    title: "Rewards (Redemption)",
    description: "List and redeem rewards",
    endpoints: [
      {
        method: "GET",
        path: "/kudoz-rewards",
        description: "List available rewards",
        params: ["limit", "offset"],
      },
      {
        method: "POST",
        path: "/kudoz-rewards/preview",
        description: "Preview reward redemption outcome",
      },
      {
        method: "POST",
        path: "/kudoz-rewards/:kudoz_reward_id/redeem",
        description: "Redeem a reward (Deducts from Wallet, Updates Ledger)",
        params: ["kudoz_reward_id"],
      },
    ],
  },
  {
    title: "Restaurants",
    description: "Manage restaurant entities in the Kudoz system",
    endpoints: [
      {
        method: "GET",
        path: "/restaurants",
        description: "List all restaurants",
      },
      {
        method: "POST",
        path: "/restaurants",
        description: "Create a new restaurant",
      },
      {
        method: "GET",
        path: "/restaurants/:restaurant_id",
        description: "Get restaurant details",
        params: ["restaurant_id"],
      },
      {
        method: "PUT",
        path: "/restaurants/:restaurant_id",
        description: "Update restaurant details",
        params: ["restaurant_id"],
      },
    ],
  },
];

export function KudozEndpoints() {
  const { toast } = useToast();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Path copied to clipboard",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
        <div>
          <h3 className="font-semibold">Base URL</h3>
          <code className="text-sm text-muted-foreground">
            {KUDOZ_BACKEND_CONFIG.baseUrl}
          </code>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard(KUDOZ_BACKEND_CONFIG.baseUrl)}
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy
        </Button>
      </div>

      <div className="grid gap-6">
        {API_GROUPS.map((group) => (
          <ApiSection
            key={group.title}
            title={group.title}
            description={group.description}
            defaultOpen={true}
          >
            <div className="grid gap-4">
              {group.endpoints.map((endpoint, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-4 p-4 rounded-lg bg-background border border-border hover:border-primary/20 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      <MethodBadge method={endpoint.method} />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-semibold">
                          {endpoint.path}
                        </code>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {endpoint.description}
                      </p>
                      {endpoint.params && endpoint.params.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {endpoint.params.map((param) => (
                            <span
                              key={param}
                              className="px-1.5 py-0.5 text-[10px] bg-muted text-muted-foreground rounded border border-border"
                            >
                              {param}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => copyToClipboard(endpoint.path)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>

                  {(endpoint.requestExample || endpoint.responseExample) && (
                    <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-border/50">
                      {endpoint.requestExample && (
                        <div>
                          <CodeBlock
                            label="Request Payload"
                            value={JSON.stringify(endpoint.requestExample, null, 2)}
                            maxHeight="300px"
                          />
                        </div>
                      )}
                      {endpoint.responseExample && (
                        <div className={!endpoint.requestExample ? "md:col-span-2" : ""}>
                          <CodeBlock
                            label="Response Example"
                            value={JSON.stringify(endpoint.responseExample, null, 2)}
                            maxHeight="300px"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ApiSection>
        ))}
      </div>
    </div>
  );
}
